import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** Rail width with icons only - the collapsed default, and the only width below `lg`. */
export const NAVIGATION_RAIL_WIDTH = '4.5rem';
/** Rail width with the labels beside the icons (200px in the design). */
export const NAVIGATION_RAIL_EXPANDED_WIDTH = '12.5rem';

interface INavigationRailState {
  /** Whether the desktop rail shows its labels. */
  isExpanded: boolean;
  /**
   * Only true once the user has toggled the rail. Width changes that aren't a click - restoring the
   * saved state on load - must snap into place; animating them reads as the menu closing and reopening.
   */
  shouldAnimate: boolean;
  toggle: () => void;
}

/**
 * A module-level store rather than state inside EventAppLayout: every event page renders its own
 * layout, so state kept there reset on each navigation and the rail visibly closed and reopened.
 * The store outlives the pages; localStorage only matters for a full reload.
 */
export const useNavigationRailStore = create<INavigationRailState>()(
  persist(
    (set) => ({
      isExpanded: false,
      shouldAnimate: false,
      toggle: () => set((state) => ({ isExpanded: !state.isExpanded, shouldAnimate: true })),
    }),
    {
      name: 'event-app-navigation-rail',
      // the animation flag is about this visit, not something to restore
      partialize: (state) => ({ isExpanded: state.isExpanded }),
      // the server renders collapsed, so the client must too until hydration is done - reading storage
      // at store creation would mismatch. rehydrateNavigationRail applies it right after.
      skipHydration: true,
    }
  )
);

/** Applies the saved state. Call from a layout effect so a reload doesn't paint the collapsed rail first. */
export function rehydrateNavigationRail() {
  useNavigationRailStore.persist.rehydrate();
}
